import { RefObject, useEffect, useState } from 'react';
import queryString from 'query-string';
import {
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SchemaFilterType,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useLazyGetDictionaryQuery,
  useLazyGetDictionaryItemsQuery,
  useBatchDeleteDictionaryItemsMutation,
} from '../../store/service';
import {
  setTakeCount as setStoreTakeCount,
  clearDictionaryItemsState,
  setSelectedItems,
} from '../../store/slice';
import { filtersSchema, schema } from '../variables/list';
import { DictionaryItem } from '../../types';

const useItems = ({
  activePermissions,
  scrollRef,
  dictionaryId,
  search,
  dictionaryItems,
  selectedItems,
}: {
  activePermissions?: PermissionEnum[];
  scrollRef: RefObject<HTMLDivElement | null>;
  dictionaryId?: string;
  search?: string;
  dictionaryItems?: DictionaryItem[];
  selectedItems?: string[];
}) => {
  const [takeCount, setTakeCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [deletePopupIsVisible, setDeletePopupIsVisible] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [getDictionary, { isLoading, isFetching }] =
    useLazyGetDictionaryQuery();
  const [
    getDictionaryItems,
    { isLoading: itemsIsLoading, isFetching: itemsIsFetching },
  ] = useLazyGetDictionaryItemsQuery();
  const [
    batchDeleteDictionaryItems,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useBatchDeleteDictionaryItemsMutation();

  const dispatch = useAppDispatch();

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const onRowsSelect = (rows: string[]) => {
    dispatch(setSelectedItems(rows));
  };

  const onDelete = async () => {
    setDeleteError('');

    const response = (await batchDeleteDictionaryItems({
      id: dictionaryId as string,
      body: {
        ids: selectedItems as string[],
      },
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    }
  };

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<DictionaryItem>({
      getList: getDictionaryItems,
      onFilterChange,
      onSortChange,
      schema,
      itemId: dictionaryId,
    });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_LIST) &&
      dictionaryId
    ) {
      getDictionary(dictionaryId);
    }
  }, [activePermissions, dictionaryId]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      !dictionaryItems &&
      !search &&
      !Object.keys(filters).length &&
      takeCount !== 0 &&
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST)
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [filters, search, dictionaryItems, takeCount, activePermissions]);

  useEffect(() => {
    if (deleteIsSuccess) {
      setDeleteError('');
      setDeletePopupIsVisible(false);

      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [deleteIsSuccess]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      !dictionaryItems &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST)
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed.search) {
          setSearchValue(parsed.search as string);
        }

        if (parsed.onlyRemoved) {
          setOnlyRemoved(true);
        }
      }

      getListAction({
        filters: filtersValues,
      });
    }
  }, [
    filters,
    search,
    dictionaryItems,
    prevFilters,
    takeCount,
    activePermissions,
  ]);

  useEffect(
    () => () => {
      dispatch(clearDictionaryItemsState());
    },
    [],
  );

  return {
    isLoading,
    isFetching,
    takeCount,
    itemsIsLoading,
    itemsIsFetching,
    searchValue,
    setSearchValue,
    filters,
    onFilter,
    errorList,
    setPage,
    filtersComponent,
    handleSubmit,
    onRowsSelect,
    deletePopupIsVisible,
    setDeletePopupIsVisible,
    onlyRemoved,
    setOnlyRemoved,
    onDelete,
    deleteInProgress,
    deleteError,
  };
};

export default useItems;
